import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { getInterventions, createIntervention, destroyIntervention, updateIntervention } from "../../../_api/Intervention";
import { useOutletContext } from "react-router-dom";
import { Button } from "../../../_helpers/components/Button";
import { dateToAAAAMMJJ, addDuration, getDuration } from "../../../_helpers/functions";
import { getInterventionsCategories } from "../../../_api/InterventionCategorie";
import { InterventionsPlanificationModal } from "./InterventionsPlanificationModal";
import { InterventionsDetailModal } from "./InterventionsDetailModal";
import { getUsers } from "../../../_api/Admin";
import { Filter } from "../../../_helpers/components/Forms/Filter";
import { InterventionsCreationModal } from "./InterventionsCreationModal";
import { cssColor } from "./InterventionsColors";

export const InterventionsList = () => {
    const [user, setUser, currentApp, currentMenu] = useOutletContext();
    const [tickets, setTickets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [families, setFamilies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showClotureModal, setShowClotureModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [intDate, setIntDate] = useState("");
    const [intTime, setIntTime] = useState("");
    const [intDuree, setIntDuree] = useState("");
    const [intUser, setIntUser] = useState("");
    const [remarks, setRemarks] = useState("");
    const [ticketStatus, setTicketStatus] = useState("");
    const [duplicate, setDuplicate] = useState(false);
    const [filter, setFilter] = useState({ status: "OPEN", priority: "ALL", team: "-1", family: "ALL", category: "-1", requester: "-1", performer: "-1" });
    const [usersCreator, setUsersCreator] = useState([]);
    const [usersEnroll, setUsersEnroll] = useState([]);

    const features_right = user.policy.rights[currentApp][currentMenu.id]["sub_menu"]["MAIN"]["features"];
    const availableTeams = user.teams.sort((a, b) => (a.name < b.name ? -1 : 1));

    const priority = {
        URGENT: {
            label: "Urgent",
            color: cssColor.URGENT,
        },
        IMPORTANT: {
            label: "Important",
            color: cssColor.IMPORTANT,
        },
        NORMALE: {
            label: "Normale",
            color: cssColor.NORMALE,
        },
        MEMO: {
            label: "Pour mémo",
            color: cssColor.MEMO,
        },
    }

    const status = {
        OPEN: {
            label: "Ouvert",
            color: cssColor.OPEN,
        },
        ONGOING: {
            label: "En cours",
            color: cssColor.ONGOING,
        },
        PENDING: {
            label: "En attente",
            color: cssColor.PENDING,
        },
        CLOSED: {
            label: "Terminé",
            color: cssColor.CLOSED,
        },
        UNREALIZABLE: {
            label: "Non réalisable",
            color: cssColor.UNREALIZABLE,
        },
    }

    const defaultTicket = {
        title: "",
        description: "",
        priority: "NORMALE",
        status: "OPEN",
        due_date: undefined,
        start_time: null,
        completion_time: null,
        requester_id: null,
        performer_id: null,
        team_id: availableTeams.length > 0 ? availableTeams[0].id : null,
        category_id: categories.length > 0 ? categories[0].id : null,
    };

    const [newTicket, setNewTicket] = useState(defaultTicket);

    const statusInput = () => {
        return {
            value: ticketStatus,
            options: [...Object.keys(status).map(s => ({ label: status[s].label, value: s }))],
            setValue: setTicketStatus,
        };
    }

    // 🔹 Charger les tickets
    const fetchTickets = async () => {
        try {
            const resp = await getInterventions();
            setTickets(resp.data);
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des interventions.");
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Charger les catégories
    const fetchCategories = async () => {
        try {
            const resp = await getInterventionsCategories();
            setCategories(resp.data);
            setFamilies(Array.from(new Set(resp.data.map((c) => { return c.family }))));
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des catégories d'interventions.");
        } finally {

        }
    };

    // Chargement des users
    const fetchUsers = async () => {
        try {
            const resp = await getUsers();
            setUsersCreator(resp.data.filter((usr) => usr.policy.rights[currentApp][currentMenu.id]["sub_menu"]["MAIN"]["features"]["task_create"] === true))
            setUsersEnroll(resp.data.filter((usr) => usr.policy.rights[currentApp][currentMenu.id]["sub_menu"]["MAIN"]["features"]["task_enroll"] === true))
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des utilisateurs.");
        } finally {

        }
    }

    useEffect(() => {
        setLoading(true);
        fetchCategories();
        fetchUsers();
        fetchTickets();
    }, []);

    // 🔹 Gestion du formulaire
    const handleChange = (e, rubrique) => {
        setNewTicket({ ...newTicket, [rubrique]: e.target.value });
    };

    // 🔹 Création de ticket
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newTicket.title.trim()) {
            toast.error("L'objet est obligatoire");
            return;
        }
        if (!newTicket.description.trim()) {
            toast.error("La description est obligatoire");
            return;
        }

        setLoading(true);
        const aTicket = { ...newTicket, requester_id: user.id };
        try {
            await createIntervention({ ...aTicket });
            toast.success("Intervention créée avec succès");
            setShowCreateModal(false);
            setNewTicket(defaultTicket);
            await fetchTickets();
        } catch (err) {
            console.error(err);
            toast.error("Impossible de créer l'intervention");
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = (ticket) => {
        const aTicket = ({
            ...defaultTicket,
            title: ticket.title,
            description: ticket.description,
            priority: ticket.priority,
            requester_id: user.id,
            performer_id: ticket.performer_id,
            category_id: ticket.category_id,
        })
        setNewTicket(aTicket);
        setShowDetailModal(false);
        setShowClotureModal(false);
        setShowCreateModal(true);
    }

    // 🔹 Suppression de ticket
    const handleDelete = async (id) => {
        if (!confirm("Supprimer cette intervention ?")) return;
        setLoading(true);
        try {
            await destroyIntervention(id);
            toast.success("Intervention supprimée");
            await fetchTickets();
        } catch {
            toast.error("Erreur lors de la suppression");
        } finally {
            setLoading(false);
            setShowDetailModal(false);
            setShowClotureModal(false);
        }
    };

    // 🔹 Ouvrir la modale de mise à jour
    const handleOpenUpdateModal = (ticket, newStatus = ticket.status) => {
        setSelectedTicket(ticket);
        setIntDate(ticket.start_time ? dateToAAAAMMJJ(ticket.start_time, "-") : dateToAAAAMMJJ(new Date(), "-"));
        setIntTime(ticket.start_time ? new Date(ticket.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        if (ticket.completion_time) {
            setIntDuree(getDuration(ticket.start_time, ticket.completion_time));
        } else {
            setIntDuree("01:00");
        }
        setRemarks("");
        setIntUser(ticket.performer_id ?? user.id);
        setTicketStatus(newStatus);
        setDuplicate(false);
        setShowClotureModal(true);
    };

    // Modal avec le détail
    const handleOpenDetailModal = (ticket) => {
        setSelectedTicket(ticket);
        setShowDetailModal(true);
    };

    const handleUpdateIntervention = async () => {
        if (!intDate || !intTime) return toast.error("Merci de renseigner la date et l’heure de début d'intervention");
        if (!intDuree) return toast.error("Merci de renseigner la durée de l'intervention");
        if (ticketStatus != "OPEN" && !intUser) return toast.error("La planification de l'intervention implique de définir une attribution.");
        if (!["OPEN", "ONGOING"].includes(ticketStatus) && !remarks) return toast.error("Merci de renseigner le résultat de l'intervention");
        setIsSaving(true);
        try {
            const startTime = new Date(intDate);
            startTime.setHours(intTime.slice(0, 2), intTime.slice(3, 5), 0);
            const endTime = addDuration(startTime, intDuree);
            const rmk = remarks ?
                remarks + (selectedTicket.remarks ? (`\n========== ${dateToAAAAMMJJ(selectedTicket.updated_at, "-")} ${new Date(selectedTicket.updated_at).toLocaleTimeString([])} ========== \n` + selectedTicket.remarks) : "")
                :
                selectedTicket.remarks
                ;
            const performer = usersEnroll.find((u) => u.id == intUser);
            const aTicket = { ...selectedTicket, start_time: new Date(startTime).toISOString(), completion_time: new Date(endTime).toISOString(), status: ticketStatus, performer_id: intUser, performer: performer, remarks: rmk };
            await updateIntervention({ ...aTicket });

            const updatedTickets = tickets.map((t) =>
                t.id === selectedTicket.id ? { ...aTicket } : t
            );
            setTickets(updatedTickets);

            toast.success("Intervention mise à jour.");

            // Fermeture des modales
            setShowDetailModal(false);
            setShowClotureModal(false);
            if (duplicate) {
                handleDuplicate(aTicket);
            }
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors de la planification");
        } finally {
            setIsSaving(false);
        }
    };

    const handleFilter = ((rub, val) => {
        setFilter({ ...filter, [rub]: val })
    });

    // 🔹 Filtrage
    const filteredTickets = tickets?.filter((t) => {
        const matchStatus = filter.status === "ALL" || t.status === filter.status;
        const matchPriority = filter.priority === "ALL" || t.priority === filter.priority;
        const matchTeam = (filter.team === "-1" && availableTeams.some(a => a.id == t.team_id)) || t.team_id?.toString() === filter.team;
        const matchFamily = filter.family === "ALL" || t.category.family === filter.family;
        const matchCategory = filter.category === "-1" || t.category_id?.toString() === filter.category;
        const matchRequester = filter.requester === "-1" || t.requester_id?.toString() === filter.requester;
        const matchPerformer = filter.performer === "-1" || t.performer_id?.toString() === filter.performer;
        return matchStatus && matchPriority && matchTeam && matchFamily && matchCategory && matchRequester && matchPerformer;
    });

    return (
        <>
            <div className="flex flex-col h-full p-6 box-border">
                {/* En-tête */}
                <div className="pb-4">
                    <div className="flex flex-wrap justify-between w-full gap-2 flex-col lg:flex-row">

                        {/* Filtres */}
                        <Filter
                            buttonLabel={
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                                </svg>

                            }
                            actualFilter={filter}
                            withLabel={true}
                            className={"flex flex-col gap-2 mb-6"}
                            listFilters={[
                                {
                                    name: "status",
                                    label: "Statut",
                                    options: [{ label: "Tous", value: "ALL" }, ...Object.keys(status).map(s => ({ label: status[s].label, value: s }))],
                                    value: filter.status,
                                },
                                {
                                    name: "priority",
                                    label: "Priorité",
                                    options: [{ label: "Toutes", value: "ALL" }, ...Object.keys(priority).map(p => ({ label: priority[p].label, value: p }))],
                                    value: filter.priority,
                                },
                                {
                                    name: "team",
                                    label: "Service",
                                    options: [{ label: "Tous", value: "-1" }, ...availableTeams.map(t => ({ label: t.name, value: t.id }))],
                                    value: filter.team,
                                },
                                {
                                    name: "family",
                                    label: "Famille",
                                    options: [{ label: "Toutes", value: "ALL" }, ...families.map(f => ({ label: f, value: f }))],
                                    value: filter.family,
                                },
                                {
                                    name: "category",
                                    label: "Catégorie",
                                    options: [{ label: "Toutes", value: "-1" }, ...categories.map(t => ({ label: t.family + " > " + t.label, value: t.id }))],
                                    value: filter.category,
                                },
                                {
                                    name: "requester",
                                    label: "Créateur",
                                    options: [{ label: "Tous", value: "-1" }, ...usersCreator.map(u => ({ label: (u.last_name ?? "") + " " + (u.first_name ?? ""), value: u.id }))],
                                    value: filter.requester,
                                },
                                {
                                    name: "performer",
                                    label: "Assignée à",
                                    options: [{ label: "Tous", value: "-1" }, ...usersEnroll.map(u => ({ label: (u.last_name ?? "") + " " + (u.first_name ?? ""), value: u.id }))],
                                    value: filter.performer,
                                },
                            ]}
                            handleChange={handleFilter}
                        />

                        <div className="flex items-center gap-2 center">
                            {features_right["task_create"] && (
                                <Button
                                    action={() => {
                                        setNewTicket(defaultTicket);
                                        setShowCreateModal(true)
                                    }}
                                    name=<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                        <title>Nouvelle demande</title>
                                    </svg>

                                />
                            )}
                        </div>
                    </div>
                    <div className="hidden lg:flex justify-center gap-2 pt-2">
                        <>
                            {filter.status == "ALL" ?
                                <span className={`text-nowrap px-2 py-1 border rounded cursor-pointer ${cssColor.ALL}`}>Tous</span>
                                :
                                <span className={`text-nowrap px-2 py-1 border rounded cursor-pointer bg-white border-gray-400 text-gray-400 hover:bg-gray-100`} onClick={(e) => handleFilter("status", "ALL")}>Tous</span>
                            }

                            {Object.keys(status).map(s => {
                                return filter.status == s ?
                                    <span key={s} className={`text-nowrap px-2 py-1 border rounded cursor-pointer ${status[s].color}`}>{status[s].label}</span>
                                    :
                                    <span key={s} className={`text-nowrap px-2 py-1 border rounded cursor-pointer bg-white border-gray-400 text-gray-400 hover:bg-gray-100`} onClick={(e) => handleFilter("status", s)}>{status[s].label}</span>

                            })}
                        </>
                    </div>
                </div>

                {/* Liste des tickets */}
                {loading ? (
                    <div className="text-center py-12 text-gray-500">Chargement...</div>
                ) : filteredTickets.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">Aucun ticket trouvé 💤</div>
                ) : (
                    <div className="flex flex-wrap lg:grid-cols-3 xl:grid-cols-5 gap-4 m-1 overflow-y-auto max-h-full justify-left">
                        {filteredTickets.map((ticket) => (
                            <div
                                key={ticket.id}
                                className="flex flex-col justify-between rounded h-42 w-full sm:w-72 shadow-sm p-2 pt-1 overflow-y-auto border hover:shadow-md transition relative overflow-hidden group"
                            >
                                <div>
                                    {ticket.team && (
                                        <div
                                            className="mb-2 h-1 -mt-1 -mx-4 rounded-full"
                                            style={{
                                                backgroundColor: ticket.team.bg_color,
                                                opacity: "70%",
                                            }}
                                        >
                                        </div>
                                    )}
                                    <div className="text-lg font-semibold text-gray-800 cursor-pointer hover:underline text-nowrap overflow-hidden text-ellipsis" onClick={() => handleOpenDetailModal(ticket)}>{ticket.title}</div>
                                    <div className="italic text-sm text-gray-500 mb-2 line-clamp-1">{ticket.category.family + " > " + ticket.category.label}</div>

                                    {ticket.performer &&
                                        <div className="text-xs text-gray-600">➡️  {ticket.performer.last_name} {ticket.performer.first_name}</div>
                                    }

                                    <div className="flex flex-col text-xxs text-gray-500 mt-2 gap-1 justify-between">
                                        {ticket.start_time && (
                                            <div> {["CLOSED", "UNREALIZABLE"].includes(ticket.status) ? "terminée" : "Prévue"} le {new Date(ticket.start_time).toLocaleDateString()} à {new Date(ticket.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-between items-center mt-4">
                                    <span className={`text-xs px-2 py-1 rounded-full border ${priority[ticket.priority].color}`}>
                                        {priority[ticket.priority].label}
                                    </span>
                                    <span className={`text-xs px-2 py-1 rounded-full border ${status[ticket.status].color}`}>
                                        {status[ticket.status].label}
                                    </span>
                                </div>

                                <div className="absolute top-2 right-2 flex justify-end gap-2 bg-white/90 rounded p-1 visible lg:invisible group-hover:visible">
                                    {((ticket.status === "OPEN" && (features_right["task_enroll"] || features_right["task_force_enroll"]))
                                        || (["ONGOING", "PENDING"].includes(ticket.status) && ticket.performer_id == user.id))
                                        && (
                                            <button
                                                onClick={() => handleOpenUpdateModal(ticket)}
                                                title="Mettre à jour l'intervention"
                                                className="cursor-pointer"
                                            >
                                                📅
                                            </button>
                                        )}

                                </div>

                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 🔹 Modal de création */}
            {showCreateModal && (
                <InterventionsCreationModal
                    newTicket={newTicket}
                    handleSubmit={handleSubmit}
                    setVisible={setShowCreateModal}
                    handleChange={handleChange}
                    categories={categories}
                    priorities={priority}
                    teams={availableTeams}
                    usersCanEnroll={features_right["task_force_enroll"] ? usersEnroll : undefined}
                    loading={loading}
                />
            )}

            {/* 🔹 Modal de clôture / mise à jour */}
            {showClotureModal &&
                <InterventionsPlanificationModal
                    title="Mettre à jour l’intervention"
                    handleSave={handleUpdateIntervention}
                    setVisible={setShowClotureModal}
                    loading={isSaving}
                    dateInput={{ label: "Date de l'intervention", value: intDate, setValue: setIntDate, isRequired: true }}
                    hourInput={{ label: "à", value: intTime, setValue: setIntTime, isRequired: true }}
                    durationInput={{ label: "Durée", value: intDuree, setValue: setIntDuree, isRequired: true }}
                    usersCanEnroll={features_right["task_force_enroll"] ? { options: usersEnroll, value: intUser, setValue: setIntUser } : undefined}
                    remarksInput={{ label: "Remarques", value: remarks, setValue: setRemarks, isRequired: true }}
                    duplicateInput={{ label: "Créer une nouvelle intervention", value: duplicate, setValue: setDuplicate }}
                    statusInput={statusInput()}
                />
            }

            {/* 🔹 Modal détail */}
            {showDetailModal &&
                <InterventionsDetailModal
                    intervention={selectedTicket}
                    setVisible={setShowDetailModal}
                    // handleOpenPlanModal={handleOpenPlanModal}
                    canEnroll={(features_right["task_enroll"] || features_right["task_force_enroll"])}
                    handleOpenUpdateModal={handleOpenUpdateModal}
                    handleDelete={handleDelete}
                    userId={user.id}
                    handleDuplicate={features_right["task_create"] ? handleDuplicate : null}
                    loading={loading}
                />
            }
        </>
    );
}
